import React, { useMemo } from 'react';
import {
    LineChart,
    Line,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    ResponsiveContainer,
    AreaChart,
    Area
} from 'recharts';
import { usePermissionContext } from '../contexts/PermissionContext';

const DashboardCharts = ({ salesData, purchaseVsPaymentData, salesChartFilter, setSalesChartFilter, purchaseChartFilter, setPurchaseChartFilter, forceShow = false }) => {
    const { hasPermission } = usePermissionContext();

    const showSales = forceShow || hasPermission('Sales Invoice', 'view');
    const showPurchase = forceShow || hasPermission('Purchase Invoice', 'view');

    const monthOptions = useMemo(() => {
        const options = [{ value: 'yearly', label: 'Full Year (Last 12 Months)' }];
        for (let i = 0; i < 12; i++) {
            const d = new Date();
            d.setDate(1);
            d.setMonth(d.getMonth() - i);
            const monthValue = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
            const monthLabel = d.toLocaleString('default', { month: 'long', year: 'numeric' });
            options.push({ value: monthValue, label: monthLabel });
        }
        return options;
    }, []);

    const getSelectedLabel = (filterValue) => {
        const opt = monthOptions.find(o => o.value === filterValue);
        if (!opt || filterValue === 'yearly') return '(Last 12 Months)';
        return `(${opt.label})`;
    };

    const FilterDropdown = ({ value, onChange }) => (
        <select 
            value={value || 'yearly'}
            onChange={(e) => onChange && onChange(e.target.value)}
            style={{
                padding: '6px 12px',
                borderRadius: '6px',
                border: '1px solid #cbd5e1',
                backgroundColor: '#fff',
                fontSize: '0.8rem',
                fontWeight: '600',
                color: '#334155',
                cursor: 'pointer',
                outline: 'none',
                boxShadow: '0 1px 2px rgba(0,0,0,0.05)'
            }}
        >
            {monthOptions.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
        </select>
    );

    if (!showSales && !showPurchase) return null;

    return (
        <div className="dashboard-charts">
            {/* 1. Area Chart (Total Sales) */}
            {showSales && (
                <div className="chart-container">
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                        <h3 className="chart-title" style={{ margin: 0 }}>
                            Total Sales {getSelectedLabel(salesChartFilter)}
                        </h3>
                        <FilterDropdown value={salesChartFilter} onChange={setSalesChartFilter} />
                    </div>
                    <div className="chart-wrapper">
                        <ResponsiveContainer width="100%" height={300}>
                            <AreaChart data={salesData} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
                                <defs>
                                    <linearGradient id="colorSales" x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="5%" stopColor="#0891b2" stopOpacity={0.8} />
                                        <stop offset="95%" stopColor="#0891b2" stopOpacity={0} />
                                    </linearGradient>
                                </defs>
                                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                                <XAxis
                                    dataKey="month"
                                    axisLine={false}
                                    tickLine={false}
                                    tick={{ fill: '#64748b', fontSize: 12 }}
                                    dy={10}
                                />
                                <YAxis
                                    axisLine={false}
                                    tickLine={false}
                                    tick={{ fill: '#64748b', fontSize: 12 }}
                                    tickFormatter={(value) => `₹${(value / 1000).toFixed(2)}k`}
                                />
                                <Tooltip
                                    contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)' }}
                                    formatter={(value) => [`₹${value.toFixed(2)}`, 'Sales']}
                                />
                                <Area
                                    type="monotone"
                                    dataKey="amount"
                                    stroke="#0891b2"
                                    fillOpacity={1}
                                    fill="url(#colorSales)"
                                    strokeWidth={3}
                                />
                            </AreaChart>
                        </ResponsiveContainer>
                    </div>
                </div>
            )}

            {/* 2. Line Chart (Purchase Invoice vs Payment) */}
            {showPurchase && (
                <div className="chart-container">
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                        <h3 className="chart-title" style={{ margin: 0 }}>
                            Purchase Invoice vs Payment {getSelectedLabel(purchaseChartFilter)}
                        </h3>
                        <FilterDropdown value={purchaseChartFilter} onChange={setPurchaseChartFilter} />
                    </div>
                    <div className="chart-wrapper">
                        <ResponsiveContainer width="100%" height={300}>
                            <LineChart data={purchaseVsPaymentData} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
                                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                                <XAxis
                                    dataKey="name"
                                    axisLine={false}
                                    tickLine={false}
                                    tick={{ fill: '#64748b', fontSize: 12 }}
                                    dy={10}
                                />
                                <YAxis
                                    axisLine={false}
                                    tickLine={false}
                                    tick={{ fill: '#64748b', fontSize: 12 }}
                                    tickFormatter={(value) => `₹${(value / 1000).toFixed(2)}k`}
                                />
                                <Tooltip
                                    contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)' }}
                                    formatter={(value) => [`₹${value.toFixed(2)}`, '']}
                                />
                                <Legend verticalAlign="top" align="right" iconType="circle" />
                                <Line
                                    type="monotone"
                                    dataKey="invoice"
                                    stroke="#7c3aed"
                                    strokeWidth={3}
                                    dot={{ r: 4, fill: '#7c3aed', strokeWidth: 2, stroke: '#fff' }}
                                    activeDot={{ r: 6 }}
                                    name="Purchase Invoice"
                                />
                                <Line
                                    type="monotone"
                                    dataKey="payment"
                                    stroke="#16a34a"
                                    strokeWidth={3}
                                    dot={{ r: 4, fill: '#16a34a', strokeWidth: 2, stroke: '#fff' }}
                                    activeDot={{ r: 6 }}
                                    name="Purchase Payment"
                                />
                            </LineChart>
                        </ResponsiveContainer>
                    </div>
                </div>
            )}
        </div>
    );
};

export default DashboardCharts;
